/**
 * /onboarding — Six-step onboarding wizard. Surface Layer (/01)
 *
 * A save-and-resume wizard. Fields hydrate from the server draft
 * (`GET /api/onboarding-draft`); each advance persists the step via
 * `saveDraft` (PATCH user_persisted_state) and shows an inline saved
 * confirmation. The final step emits an `onboarding.complete` ActionFrame.
 */

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Button,
  Column,
  Dropdown,
  Form,
  Grid,
  InlineNotification,
  PasswordInput,
  ProgressIndicator,
  ProgressStep,
  SkeletonText,
  Stack,
  TextArea,
  TextInput,
  Tile,
  Toggle,
} from '@carbon/react';
import { CheckmarkFilled, CircleDash } from '@carbon/icons-react';
import { OnboardingInterstitial, type OnboardingTrack } from '../components/onboarding/OnboardingInterstitial';
import { useDispatchStore } from '../rebuilt/action-dispatch';
import { useOnboardingDraft } from '@lib/persistence/useRuntimeData';
import { useAuthStore } from '@auth/auth-store';
import { useWebauthn, isPasskeySupported } from '@lib/auth/useWebauthn';
import { securitySetPassword } from '@auth-provider';
import { getAccessToken } from '@auth/session-token';
import { getSecurityGate, markCeremonyComplete } from '@auth/session-strength';
import { AuthWizardLayout } from '../components/auth/AuthWizardLayout';
import { OnboardingProfileWizard } from '../components/onboarding/OnboardingProfileWizard';
import type { OnboardingProfileDraft } from '@contracts/onboarding-profile.contract';
import '../styles/views.scss';

/** A single live password-requirement row (met = filled check, else dashed circle). */
function PasswordReq({ met, label }: { met: boolean; label: string }) {
  return (
    <li className={met ? 'view-reqs__item view-reqs__item--met' : 'view-reqs__item'}>
      {met ? (
        <CheckmarkFilled size={16} aria-hidden="true" />
      ) : (
        <CircleDash size={16} aria-hidden="true" />
      )}
      <span>{label}</span>
      <span className="view-reqs__sr">{met ? ' — met' : ' — not met'}</span>
    </li>
  );
}

export const Route = createFileRoute('/onboarding')({
  component: OnboardingView,
});

const STEPS = [
  { label: 'Workspace', description: 'Name your workspace' },
  { label: 'Role', description: 'How you work' },
  { label: 'First outcome', description: 'What to build first' },
  { label: 'Local AI', description: 'On-device preference' },
  { label: 'Data', description: 'Optional notes' },
  { label: 'Review', description: 'Confirm and start' },
];

const ROLE_ITEMS = ['owner', 'admin', 'developer', 'user', 'viewer'];
const OUTCOME_ITEMS = ['chat', 'document', 'report', 'query', 'automation'];

interface OnboardingFields {
  workspaceLabel: string;
  role: string;
  outcome: string;
  preferOnDevice: boolean;
  notes: string;
}

const FIELDS_FALLBACK: OnboardingFields = {
  workspaceLabel: '',
  role: 'owner',
  outcome: 'chat',
  preferOnDevice: true,
  notes: '',
};

function hydrateFields(server: Record<string, unknown> | null): Partial<OnboardingFields> | null {
  if (!server || typeof server !== 'object') return null;
  const next: Partial<OnboardingFields> = {};
  if (typeof server.workspaceLabel === 'string') next.workspaceLabel = server.workspaceLabel;
  if (typeof server.role === 'string') next.role = server.role;
  if (typeof server.outcome === 'string') next.outcome = server.outcome;
  if (typeof server.preferOnDevice === 'boolean') next.preferOnDevice = server.preferOnDevice;
  if (typeof server.notes === 'string') next.notes = server.notes;
  return Object.keys(next).length > 0 ? next : null;
}

/**
 * Security ceremony (Auth Flow v2) — runs BEFORE the business wizard when the
 * account has no password/passkey yet, or when the session arrived via a
 * magic-link (operator policy: e-mail entries always rotate the password and
 * refresh the passkey). The `new-password` autocomplete pair is what makes
 * the browser offer "Save password?" so the credential lands in the
 * Passwords app.
 */
function SecurityCeremony({
  email,
  rotation,
  onDone,
}: {
  email: string;
  rotation: boolean;
  onDone: () => void;
}) {
  const [phase, setPhase] = useState<'password' | 'passkey'>('password');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { busy: passkeyBusy, error: passkeyError, register: registerPasskey } = useWebauthn();

  // Live password requirements (mirror the RP server-side floor: ≥12 chars, ≥3
  // of 4 character classes) so the user sees what's missing before submitting.
  const classCount =
    Number(/[a-z]/.test(password)) +
    Number(/[A-Z]/.test(password)) +
    Number(/[0-9]/.test(password)) +
    Number(/[^A-Za-z0-9]/.test(password));
  const reqLength = password.length >= 12;
  const reqClasses = classCount >= 3;
  const reqMatch = password.length > 0 && password === confirm;
  const passwordReady = reqLength && reqClasses && reqMatch;

  async function submitPassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    setError(null);
    if (!reqLength || !reqClasses) {
      setError('Password too weak: at least 12 characters mixing 3 of upper/lower/number/symbol.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    const token = getAccessToken();
    if (!token) {
      setError('Your session expired — open the e-mail link again.');
      return;
    }
    setBusy(true);
    try {
      const result = await securitySetPassword(password, token);
      if (!result.ok) {
        setError(
          result.status === 'weak_password'
            ? 'Password too weak: at least 12 characters mixing upper/lower case, numbers or symbols.'
            : result.status === 'step_up_required'
              ? 'This session cannot set a password — open the e-mail sign-in link first.'
              : 'The password could not be saved. Try again.'
        );
        return;
      }
      setPhase('passkey');
    } finally {
      setBusy(false);
    }
  }

  async function enrollPasskey() {
    if (passkeyBusy) return;
    const ok = await registerPasskey(email);
    if (ok) {
      markCeremonyComplete();
      onDone();
    }
  }

  return (
    <AuthWizardLayout
      heading={rotation ? 'Refresh your security' : 'Secure your account'}
      subtitle={
        rotation
          ? 'You signed in by e-mail link: set a new password, then refresh the passkey for this device.'
          : 'Two steps: create your password, then add a passkey (Face ID / Touch ID).'
      }
    >
      <Stack gap={6}>
        <ProgressIndicator currentIndex={phase === 'password' ? 0 : 1} spaceEqually>
          <ProgressStep label="Password" description={rotation ? 'Set a new password' : 'Create your password'} />
          <ProgressStep label="Passkey" description="Secure this device" />
        </ProgressIndicator>

        {phase === 'password' ? (
          <Form aria-label={rotation ? 'Set a new password' : 'Create your password'} onSubmit={submitPassword}>
            <Stack gap={5}>
              <TextInput id="secure-email" labelText="E-mail" autoComplete="username" value={email} readOnly />
              <PasswordInput
                id="secure-new-password"
                labelText={rotation ? 'New password' : 'Password'}
                helperText="At least 12 characters, mixing upper/lower case, numbers or symbols."
                autoComplete="new-password"
                required
                value={password}
                onChange={(event) => setPassword(event.currentTarget.value)}
              />
              <PasswordInput
                id="secure-confirm-password"
                labelText="Confirm password"
                autoComplete="new-password"
                required
                value={confirm}
                onChange={(event) => setConfirm(event.currentTarget.value)}
              />
              <ul className="view-reqs" aria-label="Password requirements">
                <PasswordReq met={reqLength} label="At least 12 characters" />
                <PasswordReq met={reqClasses} label="3 of: lowercase, uppercase, number, symbol" />
                <PasswordReq met={reqMatch} label="Both passwords match" />
              </ul>
              {error ? (
                <InlineNotification kind="error" lowContrast hideCloseButton title="Password" subtitle={error} />
              ) : null}
              <Button type="submit" disabled={busy || !passwordReady}>
                {busy ? 'Saving…' : 'Save password'}
              </Button>
            </Stack>
          </Form>
        ) : (
          <Stack gap={5}>
            <p className="sign-in-card__subtitle">
              Add a passkey so next time you sign in with Face ID or Touch ID — no password.
            </p>
            {passkeyError ? (
              <>
                <InlineNotification
                  kind="error"
                  lowContrast
                  hideCloseButton
                  title="Passkey"
                  subtitle={`${passkeyError.message} (ref ${passkeyError.correlationId})`}
                />
                {passkeyError.code === 'browser_not_supported' ? (
                  <p className="sign-in-card__subtitle">
                    This browser can’t create a passkey. Open the app in Safari or Chrome on a device with
                    Face ID / Touch ID, or open a support request to finish setup.
                  </p>
                ) : null}
              </>
            ) : null}
            <Button disabled={passkeyBusy} onClick={() => void enrollPasskey()}>
              {passkeyBusy
                ? 'Waiting for passkey…'
                : passkeyError
                  ? 'Try again'
                  : rotation
                    ? 'Refresh the passkey'
                    : 'Set up the passkey'}
            </Button>
          </Stack>
        )}
      </Stack>
    </AuthWizardLayout>
  );
}

function OnboardingView() {
  const dispatch = useDispatchStore((s) => s.dispatch);
  const { draft, isLoading, tenantContextMissing, saveDraft } = useOnboardingDraft();

  const [fields, setFields] = useState<OnboardingFields>(FIELDS_FALLBACK);
  const [step, setStep] = useState(0);
  const [welcomeDismissed, setWelcomeDismissed] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ status: string; detail: string } | null>(null);
  const hydratedRef = useRef(false);

  // Passkey enrollment ("Secure this device") — the user is email-proven here.
  const email = useAuthStore((s) => s.identity?.email ?? '');
  const roles = useAuthStore((s) => s.identity?.roles) ?? [];
  const { busy: passkeyBusy, error: passkeyError, register: registerPasskey } = useWebauthn();
  const [passkeyDone, setPasskeyDone] = useState(false);
  const [profileDone, setProfileDone] = useState(false);
  const navigate = useNavigate();

  // Plane/role-aware framing: owner/admin get the Developer-Hub (operator)
  // flavour; everyone else gets the Workspace (tenant user) flavour.
  const isOperator = roles.some((r) => r === 'tenant.owner' || r === 'tenant.admin');
  const hubLabel = isOperator ? 'Developer Hub' : 'workspace';

  // Security ceremony mode (Auth Flow v2): first-login setup or e-mail-link
  // rotation runs BEFORE the business wizard.
  const [secureMode, setSecureMode] = useState<'pending' | 'setup' | 'rotation' | 'done'>('pending');
  useEffect(() => {
    let active = true;
    void getSecurityGate().then((gate) => {
      if (!active) return;
      if (!gate) {
        setSecureMode('done');
      } else if (!gate.setupComplete) {
        setSecureMode('setup');
      } else if (gate.rotationPending) {
        setSecureMode('rotation');
      } else {
        setSecureMode('done');
      }
    });
    return () => {
      active = false;
    };
  }, []);

  async function securePasskey() {
    if (!email || passkeyBusy) return;
    const ok = await registerPasskey(email);
    if (ok) setPasskeyDone(true);
  }

  // Hydrate local fields from the server draft once, when it first resolves.
  useEffect(() => {
    if (hydratedRef.current || isLoading) return;
    hydratedRef.current = true;
    const fromServer = hydrateFields(draft);
    if (fromServer) setFields((prev) => ({ ...prev, ...fromServer }));
  }, [isLoading, draft]);

  function patch(next: Partial<OnboardingFields>) {
    setFields((prev) => ({ ...prev, ...next }));
    setSavedAt(null);
  }

  async function advance() {
    setBusy(true);
    setSavedAt(null);
    const ok = await saveDraft({ step: String(step + 1), ...fields });
    // UI intents for saving drafts via persistence seam, no action frame.
    // await dispatch('onboarding.save_draft', ...
    setBusy(false);
    if (ok) {
      setSavedAt(STEPS[step]?.label ?? null);
      setSaveError(null);
      setStep((s) => Math.min(s + 1, STEPS.length - 1));
    } else {
      setSaveError('This step could not be saved — your draft was not persisted. Try again.');
    }
  }

  function back() {
    setSavedAt(null);
    setResult(null);
    setStep((s) => Math.max(s - 1, 0));
  }

  async function complete() {
    setBusy(true);
    await saveDraft({ step: String(STEPS.length), ...fields });
    // Legacy generic onboarding complete - migrate to targeted commands.
    // For now we will create the project using the workspace label.
    const entry = await dispatch(
      'project.create',
      {
        project_name: fields.workspaceLabel.trim(),
        role: fields.role,
        outcome: fields.outcome,
        prefer_on_device: fields.preferOnDevice,
        notes: fields.notes.trim(),
      },
      { label: 'Complete onboarding' }
    );
    setBusy(false);
    setResult({ status: entry.status, detail: entry.detail });
  }

  const isLast = step === STEPS.length - 1;

  // Onboarding profile (steps B–D): client/account profile, project setup, brand-profile scope.
  // Proposal-safe — draft persists; final write is an ActionFrame candidate, never a direct mutation.
  async function completeProfile(profile: OnboardingProfileDraft) {
    setBusy(true);
    await saveDraft({ step: 'profile', profile_json: JSON.stringify(profile) });
    
    if (profile.project.projectName) {
      await dispatch(
        'project.create',
        {
          project_name: profile.project.projectName ?? '',
          project_type: String(profile.project.projectType ?? ''),
          brand_scope: String(profile.brandScope.choice ?? ''),
          locale: profile.client.locale ?? 'en-US'
        },
        { label: 'Create project' }
      );
    }
    
    await navigate({ to: '/' });
    setBusy(false);
  }

  // Security ceremony first (Auth Flow v2): block the business wizard until
  // password + passkey exist; e-mail-link rotations return straight to the app.
  if (secureMode === 'pending') {
    return (
      <div className="view">
        <SkeletonText paragraph lineCount={4} />
      </div>
    );
  }
  if (secureMode === 'setup' || secureMode === 'rotation') {
    return (
      <SecurityCeremony
        email={email}
        rotation={secureMode === 'rotation'}
        onDone={() => {
          if (secureMode === 'rotation') {
            void navigate({ to: '/' });
          } else {
            setSecureMode('done');
          }
        }}
      />
    );
  }

  // After account security, collect the client/project/brand profile (steps B–D) in the login layout.
  if (!profileDone) {
    return (
      <OnboardingProfileWizard
        busy={busy}
        onSaveStep={(p) => void saveDraft({ step: 'profile', profile_json: JSON.stringify(p) })}
        onComplete={(p) => void completeProfile(p)}
      />
    );
  }

  if (!welcomeDismissed) {
    return (
      <main className="view">
        <header className="view__header">
          <h1 className="view__title">{isOperator ? 'Operator onboarding' : 'Welcome'}</h1>
          <p className="view__subtitle">Track-aware setup for {hubLabel}.</p>
        </header>
        <OnboardingInterstitial
          busy={busy}
          onBegin={(track) => {
            (window as { __onboardingTrack?: string }).__onboardingTrack = track;
            setWelcomeDismissed(true);
          }}
        />
      </main>
    );
  }

    return (
    <div className="view">
      <header className="view__header">
        <h1 className="view__title">Set up your {hubLabel}</h1>
        <p className="view__subtitle">
          {isOperator
            ? 'Operator setup — six quick steps. Progress saves after each step.'
            : 'Six quick steps. Progress saves after each step.'}
        </p>
      </header>

      <div className="surface-grid">
        <Grid condensed>
          <Column sm={4} md={8} lg={16}>
            <Tile className="view-tile">
              <ProgressIndicator currentIndex={step} spaceEqually>
                {STEPS.map((s) => (
                  <ProgressStep key={s.label} label={s.label} description={s.description} />
                ))}
              </ProgressIndicator>
            </Tile>
          </Column>

          <Column sm={4} md={8} lg={16}>
            {tenantContextMissing ? (
              <InlineNotification
                kind="info"
                lowContrast
                hideCloseButton
                title="Workspace being provisioned"
                subtitle="Onboarding resumes once your workspace context is ready."
              />
            ) : isLoading ? (
              <SkeletonText paragraph lineCount={4} />
            ) : (
              <Tile className="view-tile">
                <div className="view-stack">
            {step === 0 ? (
              <TextInput
                id="onboarding-workspace"
                labelText="Workspace label"
                placeholder="e.g. Cestari Studio workspace"
                value={fields.workspaceLabel}
                onChange={(event) => patch({ workspaceLabel: event.currentTarget.value })}
              />
            ) : null}

            {step === 1 ? (
              <Dropdown
                id="onboarding-role"
                titleText="Role"
                label="Select a role"
                items={ROLE_ITEMS}
                selectedItem={fields.role}
                itemToString={(item: string | null) => item ?? ''}
                onChange={({ selectedItem }: { selectedItem: string | null }) =>
                  selectedItem ? patch({ role: selectedItem }) : undefined
                }
              />
            ) : null}

            {step === 2 ? (
              <Dropdown
                id="onboarding-outcome"
                titleText="First outcome"
                label="Select an outcome"
                items={OUTCOME_ITEMS}
                selectedItem={fields.outcome}
                itemToString={(item: string | null) => item ?? ''}
                onChange={({ selectedItem }: { selectedItem: string | null }) =>
                  selectedItem ? patch({ outcome: selectedItem }) : undefined
                }
              />
            ) : null}

            {step === 3 ? (
              <Toggle
                id="onboarding-local-ai"
                labelText="Local AI"
                labelA="Remote approved"
                labelB="Prefer on-device"
                toggled={fields.preferOnDevice}
                onToggle={(checked: boolean) => patch({ preferOnDevice: checked })}
              />
            ) : null}

            {step === 4 ? (
              <TextArea
                id="onboarding-notes"
                labelText="Notes (optional)"
                placeholder="Anything we should know about your data or sources."
                value={fields.notes}
                onChange={(event) => patch({ notes: event.currentTarget.value })}
              />
            ) : null}

            {step === 5 ? (
              <div className="view-stack">
                <div className="view-tile__row">
                  <span className="view-tile__label">Workspace</span>
                  <strong className="view-tile__value">
                    {fields.workspaceLabel.trim() || 'Untitled workspace'}
                  </strong>
                </div>
                <div className="view-tile__row">
                  <span className="view-tile__label">Role</span>
                  <strong className="view-tile__value">{fields.role}</strong>
                </div>
                <div className="view-tile__row">
                  <span className="view-tile__label">First outcome</span>
                  <strong className="view-tile__value">{fields.outcome}</strong>
                </div>
                <div className="view-tile__row">
                  <span className="view-tile__label">Local AI</span>
                  <strong className="view-tile__value">
                    {fields.preferOnDevice ? 'Prefer on-device' : 'Remote approved'}
                  </strong>
                </div>
                <div className="view-tile__row">
                  <span className="view-tile__label">Notes</span>
                  <strong className="view-tile__value">{fields.notes.trim() || 'None'}</strong>
                </div>

                {isPasskeySupported() && email ? (
                  <div className="view-tile">
                    <strong className="view-tile__value">Secure this device</strong>
                    <span className="view-tile__label">
                      Add a passkey so next time you sign in with Face ID or Touch ID — no password.
                    </span>
                    {passkeyError ? (
                      <InlineNotification
                        kind="error"
                        lowContrast
                        hideCloseButton
                        title="Passkey"
                        subtitle={`${passkeyError.message} (ref ${passkeyError.correlationId})`}
                      />
                    ) : null}
                    {passkeyDone ? (
                      <InlineNotification
                        kind="success"
                        lowContrast
                        hideCloseButton
                        title="Passkey added"
                        subtitle="This device is secured."
                      />
                    ) : null}
                    <div className="view-actions">
                      <Button
                        kind="tertiary"
                        disabled={passkeyBusy || passkeyDone}
                        onClick={() => void securePasskey()}
                      >
                        {passkeyDone ? 'Passkey added' : passkeyBusy ? 'Waiting for passkey…' : 'Set up a passkey'}
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>

          {savedAt ? (
            <InlineNotification
              kind="success"
              lowContrast
              hideCloseButton
              title="Saved"
              subtitle={`${savedAt} step saved.`}
            />
          ) : null}

          {saveError ? (
            <InlineNotification
              kind="error"
              lowContrast
              hideCloseButton
              title="Save failed"
              subtitle={saveError}
            />
          ) : null}

          {result ? (
            <InlineNotification
              kind={result.status === 'error' || result.status === 'denied' ? 'error' : 'info'}
              lowContrast
              hideCloseButton
              title={
                result.status === 'error' || result.status === 'denied'
                  ? 'Completion failed'
                  : 'Onboarding complete'
              }
              subtitle={result.detail}
            />
          ) : null}

                <div className="view-actions">
                  <Button kind="secondary" disabled={step === 0 || busy} onClick={back}>
                    Back
                  </Button>
                  {isLast ? (
                    <Button disabled={busy} onClick={() => void complete()}>
                      Complete setup
                    </Button>
                  ) : (
                    <Button disabled={busy} onClick={() => void advance()}>
                      Save and continue
                    </Button>
                  )}
                </div>
              </Tile>
            )}
          </Column>
        </Grid>
      </div>
    </div>
  );
}
